import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getAdminReports,
  updateAdminReport,
  type AdminPageMeta,
  type AdminReportItem,
} from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/reports")({
  component: AdminReports,
});

const STATUSES = ["all", "pending", "reviewed", "resolved", "dismissed"] as const;

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  reviewed: "bg-blue-100 text-blue-800 border-blue-200",
  resolved: "bg-green-100 text-green-800 border-green-200",
  dismissed: "bg-gray-100 text-gray-600 border-gray-200",
};

function AdminReports() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("pending");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-reports", { page, pageSize, status }],
    queryFn: async () => {
      const res = await getAdminReports({ page, pageSize, status });
      if (!res.ok) throw new Error("failed to load reports");
      return (await res.json()) as { items: AdminReportItem[]; page: AdminPageMeta };
    },
    // keep the current rows visible while the next filter/page loads
    placeholderData: keepPreviousData,
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, next }: { id: string; next: string }) => {
      const res = await updateAdminReport(id, next);
      if (!res.ok) throw new Error((await res.json())?.error || "failed to update");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reports"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("report updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const meta = data?.page;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">reports</h2>
        <div className="flex items-center gap-2">
          {STATUSES.map((s) => (
            <Button
              key={s}
              variant={status === s ? "default" : "outline"}
              size="sm"
              className={
                status === s
                  ? "rounded-2xl bg-amber-500 hover:bg-amber-600 text-white"
                  : "rounded-2xl"
              }
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
        <CardContent
          className={`p-4 transition-opacity duration-200 ${
            isFetching && !isLoading ? "opacity-60" : ""
          }`}
          aria-busy={isFetching}
        >
          {isLoading ? (
            <div className="space-y-2 py-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 bg-amber-100/40 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : !data?.items.length ? (
            <div className="py-12 text-center text-gray-500">
              no {status !== "all" ? status : ""} reports
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-amber-200/50">
                  <TableHead>reported url</TableHead>
                  <TableHead>reason</TableHead>
                  <TableHead>reported user</TableHead>
                  <TableHead>reporter</TableHead>
                  <TableHead>status</TableHead>
                  <TableHead>date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={r.id} className="border-amber-100/60">
                    <TableCell>
                      <button
                        className="flex items-center gap-1 text-amber-700 hover:underline"
                        onClick={() => window.open(r.reportedUrl, "_blank")}
                        title={r.reportedUrl}
                      >
                        <span className="max-w-[180px] truncate">{r.reportedUrl}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="text-gray-900">{r.reason}</div>
                      <div
                        className="max-w-[240px] truncate text-xs text-gray-500"
                        title={r.description}
                      >
                        {r.description}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {r.reportedUsername || r.reportedEmail || r.reportedUserId}
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {r.reporterUsername || r.reporterEmail || r.reporterId}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="inline-flex items-center gap-1">
                            <Badge
                              variant="secondary"
                              className={STATUS_BADGE[r.status] || ""}
                            >
                              {r.status}
                            </Badge>
                            <ChevronDown className="h-3 w-3 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {STATUSES.filter((s) => s !== "all" && s !== r.status).map(
                            (s) => (
                              <DropdownMenuItem
                                key={s}
                                onClick={() => statusMutation.mutate({ id: r.id, next: s })}
                              >
                                mark as {s}
                              </DropdownMenuItem>
                            )
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {meta && meta.total > 0 && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-gray-600">
                showing {(meta.page - 1) * meta.pageSize + 1}-
                {Math.min(meta.page * meta.pageSize, meta.total)} of {meta.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={meta.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  previous
                </Button>
                <div className="text-sm text-gray-700">
                  page {meta.page} of {meta.totalPages}
                </div>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
